import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { AudioSystem } from '../systems/AudioSystem';
import { UIStyle } from './UIStyle';

export interface ButtonConfig {
  x: number;
  y: number;
  text: string;
  width?: number;
  height?: number;
  fontSize?: string;
  depth?: number;
  /** Extra tap padding beyond visual button edge (px). */
  touchPadding?: number;
  onClick: () => void;
}

export class Button {
  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Image;
  private hoverGlow: Phaser.GameObjects.Rectangle;
  private label: Phaser.GameObjects.Text;
  private hitZone: Phaser.GameObjects.Rectangle;
  private enabled = true;
  private onClick: () => void;
  private hitWidth: number;
  private hitHeight: number;
  private touchPadding: number;
  private activePointerId?: number;
  private destroyed = false;

  constructor(scene: Phaser.Scene, config: ButtonConfig) {
    const width = config.width ?? 280;
    const height = config.height ?? 58;
    this.touchPadding = config.touchPadding ?? (ResponsiveSystem.isMobileDevice() ? 24 : 16);
    this.hitWidth = width + this.touchPadding * 2;
    this.hitHeight = height + this.touchPadding * 2;
    const fontSize = config.fontSize ?? UIStyle.px(22);
    this.onClick = config.onClick;

    this.container = scene.add.container(config.x, config.y);
    this.container.setDepth(config.depth ?? 10);

    this.bg = scene.add.image(0, 0, TEXTURE_KEYS.UI_BUTTON);
    this.bg.setDisplaySize(width, height);

    const hoverGlow = scene.add.rectangle(0, 0, width + 10, height + 10, 0xf2c14e, 0);
    hoverGlow.setStrokeStyle(2, 0xe7c487, 0);
    this.hoverGlow = hoverGlow;

    this.label = scene.add.text(0, 0, config.text, {
      ...UIStyle.buttonLabel(fontSize),
    });
    this.label.setOrigin(0.5);

    this.hitZone = scene.add.rectangle(0, 0, this.hitWidth, this.hitHeight, 0xffffff, 0);
    this.hitZone.setInteractive({ useHandCursor: true });

    this.container.add([this.bg, this.hoverGlow, this.label, this.hitZone]);
    this.bindEvents();
  }

  private isAlive(): boolean {
    return !this.destroyed && !!this.container.scene?.sys;
  }

  private bindEvents(): void {
    const trigger = () => {
      if (!this.enabled || !this.isAlive()) return;
      AudioSystem.playSfx('UI_CLICK');
      const callback = this.onClick;
      window.setTimeout(() => {
        if (this.isAlive()) callback();
      }, 0);
    };

    const resetVisual = () => {
      if (!this.isAlive()) return;
      this.bg.clearTint();
      this.hoverGlow.setFillStyle(0x8fe3b5, 0);
      this.hoverGlow.setStrokeStyle(2, 0xf2c14e, 0);
      this.container.setScale(1);
    };

    const setHovered = (hover: boolean) => {
      if (!this.enabled || !this.isAlive()) return;
      if (hover) {
        this.bg.setTint(0xf7f7d6);
        this.hoverGlow.setFillStyle(0x8fe3b5, 0.14);
        this.hoverGlow.setStrokeStyle(2, 0xf2c14e, 0.5);
        this.container.setScale(1.02);
      } else {
        resetVisual();
      }
    };

    this.hitZone.on('pointerover', () => setHovered(true));
    this.hitZone.on('pointerdown', (_pointer: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
      event?.stopPropagation();
      if (!this.enabled || !this.isAlive()) return;
      this.activePointerId = _pointer.id;
      this.bg.setTint(0xe8f5db);
      this.hoverGlow.setFillStyle(0x8fe3b5, 0.18);
      this.hoverGlow.setStrokeStyle(2, 0xf2c14e, 0.55);
      this.container.setScale(1);
    });
    this.hitZone.on('pointerup', (_pointer: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
      event?.stopPropagation();
      if (!this.enabled || !this.isAlive() || this.activePointerId !== _pointer.id) return;
      this.activePointerId = undefined;
      trigger();
    });
    this.hitZone.on('pointerupoutside', (_pointer: Phaser.Input.Pointer) => {
      if (this.activePointerId !== _pointer.id) return;
      this.activePointerId = undefined;
      resetVisual();
    });
    this.hitZone.on('pointerout', () => {
      if (this.activePointerId !== undefined) return;
      setHovered(false);
    });
  }

  setText(text: string): void {
    if (!this.isAlive()) return;
    this.label.setText(text);
  }

  setEnabled(enabled: boolean): void {
    if (!this.isAlive()) return;
    this.enabled = enabled;
    this.bg.setAlpha(enabled ? 1 : 0.45);
    this.label.setAlpha(enabled ? 1 : 0.5);
    this.hoverGlow.setAlpha(enabled ? 1 : 0);
    if (!enabled) {
      this.activePointerId = undefined;
      this.bg.clearTint();
      this.hoverGlow.setFillStyle(0xffd966, 0);
      this.hoverGlow.setStrokeStyle(3, 0xffffff, 0);
      this.container.setScale(1);
    }
    if (enabled) {
      this.hitZone.setInteractive({ useHandCursor: true });
    } else {
      this.hitZone.disableInteractive();
    }
  }

  setVisible(visible: boolean): void {
    if (!this.isAlive()) return;
    this.container.setVisible(visible);
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  setAlpha(alpha: number): void {
    if (!this.isAlive()) return;
    this.container.setAlpha(alpha);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.activePointerId = undefined;
    if (this.hitZone.scene?.sys) {
      this.hitZone.disableInteractive();
    }
    this.container.destroy();
  }
}
